import React, { useState } from "react";
import { FiUserCheck } from "react-icons/fi";
import Modal from "../../components/common/modal/Modal";
import FormField from "../../components/common/formkit/FormField";
import { showToast } from "../../components/common/notification/Notification";
import { addContact } from "./networkApi";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { first_name: "", last_name: "", mobile_number: "", email_address: "", state_name: "", city_name: "" };

const lastTenDigits = (value) => String(value ?? "").replace(/\D/g, "").slice(-10);

/**
 * Partner > Network > "+ Add Client". Calls the existing POST /user/contact/add (addContact).
 * The contact API has no notes field, so none is offered. `existing` is the partner's current client list,
 * used to catch duplicates before the request is sent.
 */
const AddClientModal = ({ show, onClose, onSuccess, existing = [] }) => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const close = () => {
    if (submitting) return;
    setForm(EMPTY);
    setErrors({});
    onClose?.();
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const next = {};
    const mobile = form.mobile_number.trim();
    const email = form.email_address.trim().toLowerCase();

    if (!form.first_name.trim()) next.first_name = "First name is required";
    if (!mobile) next.mobile_number = "Mobile number is required";
    else if (!/^[0-9]{10}$/.test(mobile)) next.mobile_number = "Enter a valid 10-digit mobile number";
    if (email && !EMAIL_PATTERN.test(email)) next.email_address = "Enter a valid email address";

    if (!next.mobile_number && existing.some((c) => lastTenDigits(c.mobile_number || c.mobileNo) === mobile)) {
      next.mobile_number = "A client with this mobile number is already in your list";
    }
    if (
      !next.email_address &&
      email &&
      existing.some((c) => String(c.email_address || c.emailId || "").toLowerCase() === email)
    ) {
      next.email_address = "A client with this email is already in your list";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const res = await addContact({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        mobile_number: form.mobile_number.trim(),
        email_address: form.email_address.trim(),
        state_name: form.state_name.trim(),
        city_name: form.city_name.trim(),
      });

      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS" || res?.contact) {
        showToast("Client added successfully!", { type: "success" });
        setForm(EMPTY);
        setErrors({});
        onSuccess?.();
        onClose?.();
      } else {
        showToast(res?.text || res?.message || "Failed to add client", { type: "error" });
      }
    } catch (err) {
      showToast(err?.response?.data?.text || "An error occurred while adding the client", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={Boolean(show)}
      onClose={close}
      title="Add Client"
      icon={<FiUserCheck size={20} />}
      accent="green"
      footer={
        <>
          <button type="button" className="ahd-btn ahd-btn--outline" onClick={close} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" form="add-client-form" className="ahd-btn ahd-btn--primary" disabled={submitting}>
            {submitting && <span className="spinner-border spinner-border-sm" aria-hidden="true" />}
            <span>{submitting ? "Adding..." : "Add Client"}</span>
          </button>
        </>
      }
    >
      <form id="add-client-form" onSubmit={handleSubmit} noValidate>
        <div className="ahd-form-grid">
          <FormField id="client-first-name" name="first_name" label="First Name" required placeholder="e.g. Rahul" value={form.first_name} onChange={handleChange} error={errors.first_name} />
          <FormField id="client-last-name" name="last_name" label="Last Name" placeholder="e.g. Sharma" value={form.last_name} onChange={handleChange} />
          <FormField id="client-mobile" name="mobile_number" type="tel" inputMode="numeric" maxLength={10} label="Mobile Number" required placeholder="10-digit number" value={form.mobile_number} onChange={handleChange} error={errors.mobile_number} />
          <FormField id="client-email" name="email_address" type="email" label="Email" placeholder="client@example.com" value={form.email_address} onChange={handleChange} error={errors.email_address} />
          <FormField id="client-state" name="state_name" label="State" placeholder="e.g. Uttar Pradesh" value={form.state_name} onChange={handleChange} />
          <FormField id="client-city" name="city_name" label="City" placeholder="e.g. Kanpur" value={form.city_name} onChange={handleChange} />
        </div>
      </form>
    </Modal>
  );
};

export default AddClientModal;
